import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Button } from '@aws-amplify/ui-react'

import './style.css'
import classRankedLogo from '../../assets/classRankedLogo.svg'
import classRankedLogoMob from '../../assets/classrankedLogo_Mob.svg'
import { Alert, Box, Stack, TextField, Typography, useMediaQuery, useTheme, Autocomplete, InputLabel } from '@mui/material'
import { isEmpty } from 'lodash'
import { LoadingButton } from '@mui/lab'
import { useQuery } from '@tanstack/react-query'
import CustomHeader from '../../components/CustomHeader'
import PAGE_TITLES from '../../consts/titles'
import axios from 'axios'
import 'aws-amplify/auth/enable-oauth-listener'
import { notification } from '../../components/Notifications'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { grey } from '../../colors'

interface TenantListItem {
  key: string[]
  name: string
}

interface TenantListFieldProps {
  selectedTenantKey: string
  isLoading: boolean
  handleBack: () => void
  handleSubmit: () => void
  setSelectedTenantKey: React.Dispatch<React.SetStateAction<string>>
  email: string
}

function findMatchedTenant (options: TenantListItem[], email: string): TenantListItem | undefined {
  const lowerDomain = email.split('@')[1]?.toLowerCase()
  return options.find((tenant) => tenant?.key?.find(tenantKey => tenantKey.toLowerCase() === lowerDomain))
}

export const TenantListField: React.FC<TenantListFieldProps> = ({ handleBack, selectedTenantKey, isLoading, setSelectedTenantKey, handleSubmit, email }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [manualTenantError, setManualTenantError] = useState('')

  const { data: tenantList, isFetching, isError, error } = useQuery<TenantListItem[]>({
    queryKey: ['tenantList'],
    queryFn: async (): Promise<TenantListItem[]> => {
      const response = await axios.get<TenantListItem[]>('/api/tenant_info/')
      const options = Array.isArray(response.data)
        ? response.data.map((tenant: any) => {
          const key = tenant.tenant_key
          const name = tenant.name ?? key
          return { key, name }
        }).filter((t: any) => Boolean(t.key))
        : []

      if (email.split('@')[1]) {
        const lowerDomain = email.split('@')[1]?.toLowerCase()
        const matchedTenant = findMatchedTenant(options, email)
        if (matchedTenant) {
          setSelectedTenantKey(matchedTenant.key[0])
        }
      }
      return options
    },

    enabled: true
  })
  useEffect(() => {
    if (isError) {
      notification('Error fetching tenant list', { variant: 'error' })
    }
  }, [isError, error])

  // this is a temporary code block to limit the number of tenants shown in the dropdown, can be removed once backend supports search and limiting results
  // TODO: remove once backend supports search and limiting results
  const TEMP_LIMIT_TENANT_DROPDOWN = true
  const TENANT_DROPDOWN_LIMIT = 5

  const [tenantSearch, setTenantSearch] = useState('')

  const tenantOptions = useMemo(() => {
    // make a copy
    const list = tenantList ? [...tenantList] : []
    return list
  }, [tenantList])

  const getWrappedWindow = useCallback((options: TenantListItem[], startIndex: number, limit: number) => {
    if (!options) return []

    const size = Math.min(limit, options.length)
    const result: TenantListItem[] = []
    const start = startIndex >= 0 ? startIndex : 0

    for (let i = 0; i < size; i++) {
      result.push(options[(start + i) % options.length])
    }
    return result
  }, [])

  const filterTenantOptions = useCallback((options: TenantListItem[], state: { inputValue: string }) => {
    if (!TEMP_LIMIT_TENANT_DROPDOWN) return options

    const query = (state.inputValue || '').trim().toLowerCase()

    if (query) {
      return options.filter((t) => t.name.toLowerCase().includes(query) || t.key.some(k => k.toLowerCase().includes(query))).slice(0, TENANT_DROPDOWN_LIMIT)
    }

    // no search query, return wrapped window of 5 items
    const selectedIndex = selectedTenantKey ? options.findIndex((t) => t.key.includes(selectedTenantKey)) : -1
    return getWrappedWindow(options, selectedIndex >= 0 ? selectedIndex : 0, TENANT_DROPDOWN_LIMIT)
  }, [TEMP_LIMIT_TENANT_DROPDOWN, TENANT_DROPDOWN_LIMIT, getWrappedWindow, selectedTenantKey, tenantSearch])

  return (
    <>
      <CustomHeader title={PAGE_TITLES.LOGIN} />
      <Stack spacing={8} alignItems='flex-start' sx={{ mb: 5 }}>
        <Box
          component='img'
          aria-label='ClassRanked logo'
          alt='ClassRanked logo'
          src={isMobile ? classRankedLogoMob : classRankedLogo}
          sx={{
            width: isMobile ? 64 : 240,
            height: 'auto',
            display: 'block'
          }}
        />
        <Stack spacing={1.5} alignItems='flex-start'>
          <Typography
            component='h1'
            sx={{
              fontSize: { xs: '1.5rem', sm: '1.75rem' },
              fontWeight: 600,
              lineHeight: 1.25,
              color: grey[250],
              letterSpacing: '-0.01em'
            }}
          >
            Select your institution
          </Typography>
          <Typography
            sx={{
              fontSize: '0.9375rem',
              fontWeight: 400,
              lineHeight: 1.5,
              color: grey[700]
            }}
          >
            Your institution can&lsquo;t be identified from this email.
            Please select it from the list below.
          </Typography>
        </Stack>
      </Stack>

      <Box width='100%'>
        <Stack spacing={2.5}>
          {manualTenantError && <Alert severity='error'>{manualTenantError}</Alert>}
          <Box>
            <InputLabel
              sx={{
                mb: 1,
                textAlign: 'left',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: grey[900]
              }}
            >
              Institution
            </InputLabel>
            <Autocomplete
              options={tenantOptions}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.key[0] === value.key[0]}
              filterOptions={filterTenantOptions}
              renderInput={({ InputProps, ...params }) => (
                <TextField
                  {...params}
                  placeholder='Type to search...'
                  InputProps={{
                    ...InputProps,
                    startAdornment: (
                      <>
                        <SearchRoundedIcon style={{ color: grey[400] }} />
                        {InputProps.startAdornment}
                      </>
                    )
                  }}
                />
              )}
              loading={isFetching}
              size='small'
              inputValue={tenantSearch}
              onInputChange={(e, newInputValue) => {
                setTenantSearch(newInputValue)
              }}
              onChange={(e, newValue) => {
                setSelectedTenantKey(newValue?.key[0] || '')
                setManualTenantError('')
                setTenantSearch('')
              }}
              value={
                selectedTenantKey ? tenantOptions.find((opt) => opt.key.includes(selectedTenantKey)) || null : null
              }
            />
          </Box>

          <LoadingButton
            loading={isLoading || isFetching}
            size='large'
            variant='contained'
            type='submit'
            onClick={() => { handleSubmit() }}
            disabled={isLoading || isFetching || isEmpty(selectedTenantKey)}
          >
            Continue
          </LoadingButton>
          <Button
            type='button'
            variation='link'
            isFullWidth={false}
            loadingText='Back'
            onClick={() => {
              handleBack()
            }}
          >
            Back
          </Button>
        </Stack>
      </Box>
    </>
  )
}
